import { useCallback } from "react";

import type { ReservationDetailUpdateNoShowStatusInput } from "@/lib/gql/__generated__/graphql";

import { useCustomMutation } from "@/hooks/use-graphql-client";

import { notifyUserEventInfo } from "@/components/features/side-effect/notify-user-event-info";
import { useLoadingScreen } from "@/components/features/side-effect/show-loading-screen-to-user/_hooks/use-loading-screen";

import { RESERVATION_DETAIL_UPDATE_NO_SHOW_STATUS_FOR_MANAGEMENT_RESERVATION_ITEM_DETAIL_UPDATE_MUTATION } from "./schema";
import * as Shared from "../../../../_shared";

export const useReservationDetailUpdateNoShowStatusUpdateMutation = () => {
	const { mutationFunc } = useCustomMutation(
		RESERVATION_DETAIL_UPDATE_NO_SHOW_STATUS_FOR_MANAGEMENT_RESERVATION_ITEM_DETAIL_UPDATE_MUTATION,
	);
	const { showLoadingScreenCallback, hideLoadingScreenCallback } = useLoadingScreen();

	const onUpdateReservationDetailUpdateNoShow = useCallback(
		(input: ReservationDetailUpdateNoShowStatusInput) => {
			showLoadingScreenCallback();
			return mutationFunc({
				variables: { input },
				update: (cache, { data }) => {
					/**
					 * キャッシュから予約詳細を取得
					 */
					const cached = cache.readQuery({
						query: Shared.Model.Repository.Query.RESERVATION_DETAIL_FOR_MANAGEMENT_RESERVATION_ITEM_QUERY,
						variables: { hotelID: input.hotelID, reservationId: input.reservationID },
					});
					/**
					 * レスポンスから更新されたフィールドを取得
					 */
					const updated = data?.reservationDetailUpdateNoShowStatus.reservationDetail;
					if (!cached || !updated) return;

					/**
					 * 予約更新のキャッシュを更新
					 * NO SHOW 設定により canCancel も連動して変わるため両フィールドを更新する
					 */
					cache.writeQuery({
						query: Shared.Model.Repository.Query.RESERVATION_DETAIL_FOR_MANAGEMENT_RESERVATION_ITEM_QUERY,
						variables: { hotelID: input.hotelID, reservationId: input.reservationID },
						data: {
							reservationDetail: {
								...cached.reservationDetail,
								statusNoShow: updated.statusNoShow,
								canCancel: updated.canCancel,
							},
							repeaterReservationList: cached.repeaterReservationList,
						},
					});
				},
			})
				.then(({ errors }) => {
					if (!errors) {
						notifyUserEventInfo({
							variant: "default",
							title: "お客様のステータスを更新しました",
						});
					}
				})
				.finally(() => {
					hideLoadingScreenCallback();
				});
		},
		[hideLoadingScreenCallback, mutationFunc, showLoadingScreenCallback],
	);

	return {
		onUpdateReservationDetailUpdateNoShow,
	};
};
